export type ConversionType = 
  | 'pdf-to-word'
  | 'pdf-to-excel'
  | 'pdf-to-ppt'
  | 'pdf-to-jpg'
  | 'image-to-pdf'
  | 'merge'
  | 'split'
  | 'compress';

export interface ConversionProgress {
  stage: 'uploading' | 'converting' | 'downloading';
  percent: number;
}

export interface ConversionResult {
  downloadUrl: string;
  fileName: string;
  fileSize?: number;
  compressedSize?: number;
}

export interface ConversionError {
  code: 'FILE_TOO_LARGE' | 'INVALID_FORMAT' | 'NETWORK_ERROR' | 'CONVERSION_FAILED' | 'UNKNOWN';
  message: string;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

// ConvertAPI 配置
const CONVERT_API_SECRET = import.meta.env.VITE_CONVERT_API_SECRET || 'demo_secret';
const CONVERT_API_BASE = 'https://v2.convertapi.com';

/**
 * 验证文件大小和格式
 */
export function validateFile(file: File, allowedTypes: string[]): ConversionError | null {
  if (file.size > MAX_FILE_SIZE) {
    return {
      code: 'FILE_TOO_LARGE',
      message: `File size exceeds 20MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`
    };
  }

  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  if (!fileExtension || !allowedTypes.includes(fileExtension)) {
    return {
      code: 'INVALID_FORMAT',
      message: `Invalid file format. Allowed: ${allowedTypes.join(', ')}`
    };
  }

  return null;
}

/**
 * 获取转换类型对应的 API 路径
 */
function getConversionPath(conversionType: ConversionType, sourceFormat?: string): string {
  switch (conversionType) {
    case 'pdf-to-word':
      return '/pdf/to/docx';
    case 'pdf-to-excel':
      return '/pdf/to/xlsx';
    case 'pdf-to-ppt':
      return '/pdf/to/pptx';
    case 'pdf-to-jpg':
      return '/pdf/to/jpg';
    case 'image-to-pdf':
      return `/${sourceFormat || 'jpg'}/to/pdf`;
    case 'merge':
      return '/pdf/to/merge';
    case 'split':
      return '/pdf/to/split';
    case 'compress':
      return '/pdf/to/compress';
    default:
      return '/pdf/to/docx';
  }
}

/**
 * 获取输出文件扩展名
 */
function getOutputExtension(conversionType: ConversionType): string {
  switch (conversionType) {
    case 'pdf-to-word':
      return 'docx';
    case 'pdf-to-excel':
      return 'xlsx';
    case 'pdf-to-ppt':
      return 'pptx';
    case 'pdf-to-jpg':
      return 'jpg';
    case 'image-to-pdf':
    case 'merge':
    case 'split':
    case 'compress':
      return 'pdf';
    default:
      return 'pdf';
  }
}

/**
 * 使用 XMLHttpRequest 上传文件并跟踪进度
 */
function uploadFileWithProgress(
  url: string,
  formData: FormData,
  onProgress: (percent: number) => void
): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // 监听上传进度
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 50); // 上传占 0-50%
        onProgress(percent);
      }
    });

    // 监听响应
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (error) {
          reject({
            code: 'CONVERSION_FAILED',
            message: 'Invalid API response'
          });
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject({
            code: 'CONVERSION_FAILED',
            message: error.Message || `API error: ${xhr.status}`
          });
        } catch {
          reject({
            code: 'CONVERSION_FAILED',
            message: `API error: ${xhr.status}`
          });
        }
      }
    });

    // 监听错误
    xhr.addEventListener('error', () => {
      reject({
        code: 'NETWORK_ERROR',
        message: 'Network error occurred'
      });
    });

    xhr.open('POST', url);
    xhr.send(formData);
  });
}

/**
 * 真实文件转换
 */
export async function convertFile(
  file: File,
  conversionType: ConversionType,
  onProgress?: (progress: ConversionProgress) => void
): Promise<ConversionResult> {
  try {
    // 获取源文件格式
    const sourceFormat = file.name.split('.').pop()?.toLowerCase() || 'pdf';
    
    // 构建 API URL
    const apiPath = getConversionPath(conversionType, sourceFormat);
    const apiUrl = `${CONVERT_API_BASE}${apiPath}?Secret=${CONVERT_API_SECRET}`;

    // 准备表单数据
    const formData = new FormData();
    formData.append('File', file);

    // 如果是分割操作，添加页面范围参数（这里需要从外部传入，暂时使用默认值）
    if (conversionType === 'split') {
      formData.append('PageRange', '1-end');
    }

    // 上传文件（0-50%）
    onProgress?.({ stage: 'uploading', percent: 0 });
    const response = await uploadFileWithProgress(apiUrl, formData, (percent) => {
      onProgress?.({ stage: 'uploading', percent });
    });

    // 转换中（50-90%）
    onProgress?.({ stage: 'converting', percent: 50 });
    await simulateProgress((percent) => {
      onProgress?.({ stage: 'converting', percent: 50 + percent * 0.4 });
    }, 1000);

    // 检查响应
    if (!response.Files || response.Files.length === 0) {
      throw {
        code: 'CONVERSION_FAILED',
        message: 'No output file received'
      };
    }

    const outputFile = response.Files[0];
    const downloadUrl = outputFile.Url;
    const fileSize = outputFile.FileSize;

    // 下载文件（90-100%）
    onProgress?.({ stage: 'downloading', percent: 90 });
    const blob = await fetch(downloadUrl).then(res => res.blob());
    const localUrl = URL.createObjectURL(blob);

    onProgress?.({ stage: 'downloading', percent: 100 });

    // 生成输出文件名
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const outputExtension = getOutputExtension(conversionType);
    const fileName = `${baseName}.${outputExtension}`;

    return {
      downloadUrl: localUrl,
      fileName,
      fileSize: blob.size,
      compressedSize: conversionType === 'compress' ? fileSize : undefined
    };
  } catch (error) {
    if ((error as ConversionError).code) {
      throw error;
    }
    throw {
      code: 'CONVERSION_FAILED',
      message: error instanceof Error ? error.message : 'Conversion failed'
    } as ConversionError;
  }
}

/**
 * 模拟进度更新（用于转换阶段）
 */
function simulateProgress(
  onProgress: (percent: number) => void,
  duration: number
): Promise<void> {
  return new Promise((resolve) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      onProgress(Math.min(progress / 100, 1));
      if (progress >= 100) {
        clearInterval(interval);
        resolve();
      }
    }, duration / 10);
  });
}

/**
 * 触发文件下载
 */
export function downloadFile(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * 清理下载URL
 */
export function cleanupDownloadUrl(url: string): void {
  if (url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * 批量转换文件（用于合并PDF等场景）
 */
export async function convertMultipleFiles(
  files: File[],
  conversionType: ConversionType,
  onProgress?: (progress: ConversionProgress) => void
): Promise<ConversionResult> {
  try {
    // 验证所有文件
    for (const file of files) {
      const error = validateFile(file, ['pdf']);
      if (error) {
        throw error;
      }
    }

    // 构建 API URL
    const apiPath = getConversionPath(conversionType);
    const apiUrl = `${CONVERT_API_BASE}${apiPath}?Secret=${CONVERT_API_SECRET}`;

    // 准备表单数据
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`Files[${index}]`, file);
    });

    // 上传文件（0-50%）
    onProgress?.({ stage: 'uploading', percent: 0 });
    const response = await uploadFileWithProgress(apiUrl, formData, (percent) => {
      onProgress?.({ stage: 'uploading', percent });
    });

    // 转换中（50-90%）
    onProgress?.({ stage: 'converting', percent: 50 });
    await simulateProgress((percent) => {
      onProgress?.({ stage: 'converting', percent: 50 + percent * 0.4 });
    }, 1500);

    // 检查响应
    if (!response.Files || response.Files.length === 0) {
      throw {
        code: 'CONVERSION_FAILED',
        message: 'No output file received'
      };
    }

    const outputFile = response.Files[0];
    const downloadUrl = outputFile.Url;

    // 下载文件（90-100%）
    onProgress?.({ stage: 'downloading', percent: 90 });
    const blob = await fetch(downloadUrl).then(res => res.blob());
    const localUrl = URL.createObjectURL(blob);

    onProgress?.({ stage: 'downloading', percent: 100 });

    return {
      downloadUrl: localUrl,
      fileName: 'merged.pdf',
      fileSize: blob.size
    };
  } catch (error) {
    if ((error as ConversionError).code) {
      throw error;
    }
    throw {
      code: 'CONVERSION_FAILED',
      message: error instanceof Error ? error.message : 'Conversion failed'
    } as ConversionError;
  }
}